import { useQuery } from '@tanstack/react-query';

import { useSummarizationId } from 'hooks/useSummarizationId';
import { attachmentsQueryKeys, showAttachment } from 'services/api/attachments';

/**
 * Query definition for an attachment's signed view-only URL. Shared by the
 * declarative hook below and the imperative click-to-open path
 * (`useClickAttachment`) so both fetch and cache identically.
 */
export function attachmentSignedUrlQuery(attachmentId: string, attachableModel?: string) {
  return {
    queryKey: attachmentsQueryKeys.show(attachmentId),
    queryFn: () =>
      showAttachment({
        id: attachmentId,
        include: 'url',
        viewOnly: true,
        ...(attachableModel ? { attachableModel } : {}),
      }),
  };
}

/** Falls back to the summarization context when no attachable model is given. */
export function useResolvedAttachableModel(attachableModel?: string) {
  const summarizationId = useSummarizationId();

  return attachableModel ?? (summarizationId ? 'summarization' : undefined);
}

/**
 * Low-level signed-URL fetch for an attachment, used by the inline-preview
 * hooks (`useAttachmentUrl`, `ImageViewer`).
 *
 * Pass `enabled: false` to defer the request until the caller is ready (e.g.,
 * first hover, first open). By default the URL is treated as fresh for 29s —
 * just inside the server-side signature lifetime measured from *signing*, so
 * a "fresh" cached URL can still have expired in transit. Consumers that
 * navigate to or render the URL some time after fetching should pass
 * `staleTime: 0` and gate on `isFetching` so hydrated cache data is never
 * used directly.
 */
export function useAttachmentSignedUrl(
  attachmentId: string,
  attachableModel?: string,
  options: { enabled?: boolean; staleTime?: number } = {}
) {
  const { enabled = true, staleTime = 29 * 1000 } = options;
  const resolvedAttachableModel = useResolvedAttachableModel(attachableModel);

  return useQuery({
    ...attachmentSignedUrlQuery(attachmentId, resolvedAttachableModel),
    enabled: enabled && !!attachmentId,
    staleTime,
  });
}
